import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useLanguage } from '../contexts/LanguageContext';

interface PlanInfo {
  name: string;
  plan_id: string;
  price_mxn: number;
  fee_percentage: number;
  max_active_tandas: number;
  max_members: number;
  features: string[];
}

interface Subscription {
  id: number;
  plan: string;
  status: string;
  started_at: string;
  expires_at: string | null;
}

const Planes: React.FC = () => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [currentSub, setCurrentSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        axios.get('/plans'),
        axios.get('/subscriptions/me').catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data);
      setCurrentSub(subRes.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    try {
      setSubscribing(true);
      await axios.post('/subscriptions', { plan: planId });
      enqueueSnackbar(t('common.success'), { variant: 'success' });
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || t('common.error'), { variant: 'error' });
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    try {
      setSubscribing(true);
      await axios.delete('/subscriptions/me');
      enqueueSnackbar(t('common.success'), { variant: 'success' });
      fetchData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || t('common.error'), { variant: 'error' });
    } finally {
      setSubscribing(false);
    }
  };

  const currentPlan = currentSub?.plan || 'free';

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'free': return <CheckCircleIcon sx={{ fontSize: 40 }} />;
      case 'premium': return <StarIcon sx={{ fontSize: 40 }} />;
      case 'business': return <BusinessIcon sx={{ fontSize: 40 }} />;
      default: return <CheckCircleIcon sx={{ fontSize: 40 }} />;
    }
  };

  const getPlanColor = (planId: string) => {
    switch (planId) {
      case 'free': return '#4caf50';
      case 'premium': return '#ff9800';
      case 'business': return '#1976d2';
      default: return '#4caf50';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  const faqs = [
    { q: t('plans.faq1q'), a: t('plans.faq1a') },
    { q: t('plans.faq2q'), a: t('plans.faq2a') },
    { q: t('plans.faq3q'), a: t('plans.faq3a') },
  ];

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          {t('plans.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
          {t('plans.subtitle')}
        </Typography>
      </Box>

      {/* Plan Cards */}
      <Grid container spacing={4} sx={{ mb: 5, mt: 1 }} justifyContent="center" alignItems="stretch">
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan === plan.plan_id;
          const isPremium = plan.plan_id === 'premium';

          return (
            <Grid item xs={12} sm={6} md={4} key={plan.plan_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'visible',
                  border: isPremium ? '2px solid' : '1px solid',
                  borderColor: isPremium ? 'warning.main' : 'divider',
                  transform: isPremium ? 'scale(1.05)' : 'none',
                  zIndex: isPremium ? 1 : 0,
                  boxShadow: isPremium ? 6 : 1,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: isPremium ? 10 : 4,
                  },
                }}
              >
                {isPremium && (
                  <Chip
                    label={t('plans.popular')}
                    color="warning"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 'bold',
                      zIndex: 2,
                    }}
                  />
                )}

                {isCurrentPlan && (
                  <Chip
                    label={t('plans.currentPlan')}
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: isPremium ? -14 : -14,
                      right: 16,
                      fontWeight: 'bold',
                      zIndex: 2,
                    }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1, pt: 4, pb: 1 }}>
                  {/* Plan Icon */}
                  <Box
                    sx={{
                      textAlign: 'center',
                      mb: 2,
                      color: getPlanColor(plan.plan_id),
                      '& svg': {
                        filter: isPremium ? 'drop-shadow(0 2px 4px rgba(255,152,0,0.4))' : 'none',
                      },
                    }}
                  >
                    {getPlanIcon(plan.plan_id)}
                  </Box>

                  {/* Plan Name */}
                  <Typography variant="h5" align="center" gutterBottom fontWeight="bold">
                    {plan.name}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {plan.price_mxn === 0 ? (
                      <Typography variant="h4" color="text.primary" fontWeight="bold">
                        {t('plans.free')}
                      </Typography>
                    ) : (
                      <Box>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          component="span"
                          sx={{ color: getPlanColor(plan.plan_id) }}
                        >
                          ${plan.price_mxn}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {' '}MXN{t('plans.perMonth')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Key Stats */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, py: 1, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2">{t('plans.commission')}</Typography>
                      <Typography variant="body2" fontWeight="bold" color={plan.fee_percentage === 0 ? 'success.main' : 'text.primary'}>
                        {plan.fee_percentage === 0 ? t('plans.free') : `${plan.fee_percentage}%`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, py: 1, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2">{t('plans.activeTandas')}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {plan.max_active_tandas >= 999 ? t('plans.unlimited') : plan.max_active_tandas}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2">{t('plans.membersPerTanda')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{plan.max_members}</Typography>
                    </Box>
                  </Box>

                  {/* Features */}
                  <List dense disablePadding>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: getPlanColor(plan.plan_id) }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                {/* Action Button */}
                <Box sx={{ p: 2, pt: 1 }}>
                  {isCurrentPlan ? (
                    <Button variant="outlined" fullWidth disabled size="large">
                      {t('plans.currentLabel')}
                    </Button>
                  ) : (
                    <Button
                      variant={isPremium ? 'contained' : 'outlined'}
                      color={isPremium ? 'warning' : 'primary'}
                      fullWidth
                      size="large"
                      onClick={() => {
                        if (plan.plan_id === 'free') {
                          handleCancel();
                        } else {
                          handleSubscribe(plan.plan_id);
                        }
                      }}
                      disabled={subscribing}
                      sx={{
                        fontWeight: 'bold',
                        ...(isPremium && {
                          boxShadow: '0 4px 12px rgba(255,152,0,0.4)',
                        }),
                      }}
                    >
                      {t('plans.selectPlan')}
                    </Button>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* FAQ */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom align="center" fontWeight="bold" sx={{ mb: 3 }}>
          {t('plans.faq')}
        </Typography>
        {faqs.map((faq, idx) => (
          <Accordion key={idx} variant="outlined" sx={{ '&:before': { display: 'none' }, mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>{faq.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
};

export default Planes;
